import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Circle,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  Leaf,
  Thermometer,
  Eye,
  Calendar,
  Stack,
  CaretDown,
  CaretUp,
  Lightning,
  Globe,
  ArrowsOut,
  ArrowsIn,
  Info,
  Compass,
  ChartBar,
  Camera,
  Crosshair,
  BookmarkSimple,
  Plus,
  Minus,
  Trash,
  X,
  Mountains,
  Timer,
  Bell,
  CloudSun,
  Rocket,
} from "@phosphor-icons/react";
import SearchBar from "@/components/dashboard/SearchBar";
import SnapshotModal from "@/components/dashboard/SnapshotModal";
import AnalyticsDrawer from "@/components/dashboard/AnalyticsDrawer";
import TimeLapseControl from "@/components/dashboard/TimeLapseControl";
import DownloadModal from "@/components/dashboard/DownloadModal";
import AlertSettings, { loadAlertSettings } from "@/components/dashboard/AlertSettings";
import MultiLayerPanel from "@/components/dashboard/MultiLayerPanel";
import NotificationBell from "@/components/dashboard/NotificationBell";
import BrandLogo from "@/components/BrandLogo";
import NewsTicker from "@/components/dashboard/NewsTicker";
import SpacePanel, { SpaceMapOverlay } from "@/components/dashboard/SpacePanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default radius (km) around the user's real-time GPS position that Follow-me
// mode uses to filter "nearby events" in the sidebar / mobile sheet.
const FOLLOW_RADIUS_KM = 500;
const SAVED_LOCATIONS_KEY = "conexus_saved_locations";

const GIBS_LAYERS = [
  {
    id: "ndvi",
    name: "Vegetation (NDVI)",
    layer: "MODIS_Terra_NDVI_8Day",
    format: "png",
    maxZoom: 9,
    description: "MODIS Terra 8-day vegetation index composite",
    Icon: Leaf,
    accent: "text-emerald-400",
    accentBg: "bg-emerald-500/20",
  },
  {
    id: "truecolor",
    name: "True Color",
    layer: "MODIS_Terra_CorrectedReflectance_TrueColor",
    format: "jpg",
    maxZoom: 9,
    description: "Natural color from MODIS Terra",
    Icon: Eye,
    accent: "text-sky-400",
    accentBg: "bg-sky-500/20",
  },
  {
    id: "lst",
    name: "Surface Temp",
    layer: "MODIS_Terra_Land_Surface_Temp_Day",
    format: "png",
    maxZoom: 7,
    description: "Daytime land surface temperature",
    Icon: Thermometer,
    accent: "text-orange-400",
    accentBg: "bg-orange-500/20",
  },
];

function getGibsTileUrl(layerConfig, date) {
  return `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/${layerConfig.layer}/default/${date}/GoogleMapsCompatible_Level${layerConfig.maxZoom}/{z}/{y}/{x}.${layerConfig.format}`;
}

function getDefaultDate() {
  const d = new Date();
  d.setDate(d.getDate() - 3);
  return d.toISOString().split("T")[0];
}

const EVENT_COLORS = {
  wildfires: "#ef4444",
  severeStorms: "#8b5cf6",
  volcanoes: "#f97316",
  seaLakeIce: "#38bdf8",
  earthquakes: "#eab308",
  floods: "#3b82f6",
  default: "#94a3b8",
};

function getEventColor(categories) {
  if (!categories?.length) return EVENT_COLORS.default;
  return EVENT_COLORS[categories[0].id] || EVENT_COLORS.default;
}

function getMagColor(mag) {
  if (mag >= 7) return "#991b1b";
  if (mag >= 6) return "#dc2626";
  if (mag >= 5) return "#ef4444";
  if (mag >= 4) return "#f97316";
  if (mag >= 3) return "#fb923c";
  return "#facc15";
}

// ─── Mobile events bottom-sheet (drag-to-expand) ─────────────
// A phone-only sheet that snaps between three heights:
//   • peek  — 48px  (just the handle + "Events (N)" title)
//   • half  — 42vh  (default open, half the screen)
//   • full  — 82vh  (nearly full screen, keeps just the map date/layer visible)
// Drag the top handle up/down; a >60px flick advances the state, and taps
// on the handle bar cycle peek → half → full → peek.
function MobileEventsSheet({ state, setState, events, loading, onEventClick }) {
  const HEIGHTS = { peek: 48, half: "42vh", full: "82vh" };
  const cycle = () => {
    setState(state === "peek" ? "half" : state === "half" ? "full" : "peek");
  };
  const onDragEnd = (_e, info) => {
    // negative offset.y = dragged up (expand); positive = dragged down (collapse)
    if (info.offset.y < -60) {
      setState(state === "peek" ? "half" : "full");
    } else if (info.offset.y > 60) {
      setState(state === "full" ? "half" : "peek");
    }
  };
  return (
    <motion.div
      // Anchored above the ~56px mobile toolbar; hidden on ≥sm screens.
      className="sm:hidden absolute left-0 right-0 z-[905] bg-stone-900/97 backdrop-blur-md border-t border-x border-stone-700 rounded-t-2xl shadow-2xl overflow-hidden flex flex-col"
      style={{ bottom: 56 }}
      animate={{ height: HEIGHTS[state] }}
      transition={{ type: "spring", stiffness: 320, damping: 34 }}
      drag="y"
      dragElastic={0.2}
      dragConstraints={{ top: 0, bottom: 0 }}
      onDragEnd={onDragEnd}
      data-testid="mobile-events-sheet"
      data-sheet-state={state}
    >
      {/* Handle + header (whole strip is a tap target that cycles heights) */}
      <button
        onClick={cycle}
        className="w-full py-2 flex flex-col items-center gap-1 shrink-0"
        data-testid="mobile-events-sheet-handle"
        aria-label={`Events sheet: ${state}. Tap to expand`}
      >
        <span className="block w-10 h-1 rounded-full bg-stone-600" />
        <div className="flex items-center gap-2 text-white">
          <Lightning size={14} weight="bold" className="text-amber-400" />
          <span className="text-sm font-heading">Events</span>
          {events.length > 0 && (
            <span className="text-xs text-stone-400">({events.length})</span>
          )}
          {state === "peek" ? (
            <CaretUp size={12} className="text-stone-500" />
          ) : (
            <CaretDown size={12} className="text-stone-500" />
          )}
        </div>
      </button>

      {/* Body (hidden in peek) */}
      {state !== "peek" && (
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-8 text-center">
              <div className="w-6 h-6 border-2 border-stone-600 border-t-emerald-400 rounded-full animate-spin mx-auto" />
              <p className="text-xs text-stone-500 mt-3 font-body">Loading events...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-xs text-stone-500 font-body">No active events</p>
            </div>
          ) : (
            events.slice(0, 40).map((event) => {
              const geom = event.geometry?.[event.geometry.length - 1];
              const color = (function () {
                const map = { wildfires: "#ef4444", severeStorms: "#8b5cf6", volcanoes: "#f97316", seaLakeIce: "#38bdf8", earthquakes: "#eab308", floods: "#3b82f6" };
                return map[event.categories?.[0]?.id] || "#94a3b8";
              })();
              return (
                <button
                  key={event.id}
                  className="w-full text-left px-4 py-3 border-b border-stone-800/50 last:border-0 flex items-start gap-3 hover:bg-stone-800/60 active:bg-stone-800 transition-colors"
                  onClick={() => onEventClick(event)}
                  data-testid={`mobile-event-item-${event.id}`}
                >
                  <div className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-body truncate">{event.title}</p>
                    <p className="text-[11px] text-stone-500 mt-0.5">
                      {event.categories?.[0]?.title}
                      {geom?.date && <span className="ml-2">· {new Date(geom.date).toLocaleDateString()}</span>}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </motion.div>
  );
}

// ─── Mobile bottom-toolbar button ────────────────────────────
// Compact icon-plus-label button used inside the mobile drawer only.
// activeColor names a Tailwind hue (emerald / orange / sky / amber / stone) so
// the on-state ring / accent stays consistent with the desktop vertical stack.
function MobileToolBtn({ children, active, activeColor = "emerald", onClick, label, testId }) {
  const activeRing = {
    emerald: "border-emerald-500 text-emerald-400",
    orange:  "border-orange-500 text-orange-400",
    sky:     "border-sky-500 text-sky-400",
    amber:   "border-amber-500 text-amber-400",
    stone:   "border-stone-500 text-white",
  }[activeColor] || "border-emerald-500 text-emerald-400";
  return (
    <button
      onClick={onClick}
      className={`shrink-0 flex flex-col items-center justify-center gap-0.5 min-w-[54px] px-2 py-1.5 rounded-lg border transition-colors ${
        active ? `${activeRing} bg-stone-800/70` : "border-stone-700 text-stone-400 bg-stone-900/70"
      }`}
      data-testid={testId}
      aria-label={label}
    >
      {children}
      <span className="text-[9px] leading-tight font-body">{label}</span>
    </button>
  );
}

// ─── Map sub-components ──────────────────────────────────────

// Great-circle distance (haversine) in km between two lat/lon points.
function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Recenters the map on the given position whenever it changes, but ONLY while
// follow-me mode is on. Uses `flyTo` for a smooth pan/zoom on first lock and
// `setView` for subsequent GPS updates so continuous tracking feels stable.
function FollowMeCenter({ position, active, targetZoom = 12 }) {
  const map = useMap();
  const firstFixRef = useRef(true);
  useEffect(() => {
    if (!active || !position) { firstFixRef.current = true; return; }
    if (firstFixRef.current) {
      map.flyTo(position, targetZoom, { duration: 1.2 });
      firstFixRef.current = false;
    } else {
      map.setView(position, map.getZoom(), { animate: true });
    }
  }, [active, position, map, targetZoom]);
  return null;
}

// Compact +/- zoom control that lives inside our custom toolbar (Leaflet's
// built-in control is hidden via `zoomControl={false}`). Rendered inside the
// MapContainer so it can use `useMap()`; positioned absolutely relative to
// `.leaflet-container` which is `position: relative` by default.
function ZoomControl() {
  const map = useMap();
  return (
    <div
      className="flex absolute top-1/2 -translate-y-1/2 right-2 sm:right-4 z-[900] flex-col gap-1 pointer-events-auto"
      style={{ position: "absolute" }}
      data-testid="zoom-controls"
    >
      <button
        onClick={() => map.zoomIn()}
        className="bg-stone-900/90 backdrop-blur-md border border-stone-700 rounded-xl p-2.5 text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
        title="Zoom in"
        data-testid="zoom-in-btn"
      >
        <Plus size={18} weight="bold" />
      </button>
      <button
        onClick={() => map.zoomOut()}
        className="bg-stone-900/90 backdrop-blur-md border border-stone-700 rounded-xl p-2.5 text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
        title="Zoom out"
        data-testid="zoom-out-btn"
      >
        <Minus size={18} weight="bold" />
      </button>
    </div>
  );
}

// Zoom-aware label overlay: at zoom ≤ 8 we use CARTO's `dark_only_labels`
// (sparse country / region names in white with dark halo — great over
// the NDVI green). Past zoom 8 we swap to `voyager_only_labels` which
// packs in cities, towns and neighborhoods so field workers can find
// villages at a glance.
function AdaptiveLabelsLayer() {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useMapEvents({
    zoomend: () => setZoom(map.getZoom()),
  });
  const url =
    zoom >= 9
      ? "https://basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png"
      : "https://basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png";
  return (
    <TileLayer
      key={zoom >= 9 ? "voyager-labels" : "dark-labels"}
      url={url}
      attribution='Labels &copy; <a href="https://carto.com/attributions">CARTO</a>'
      subdomains="abcd"
      maxZoom={20}
      detectRetina={true}
      zIndex={650}
    />
  );
}

function MapFlyTo({ center, zoom }) {
  const map = useMap();
  const prev = useRef(center);
  useEffect(() => {
    if (center && (center[0] !== prev.current[0] || center[1] !== prev.current[1])) {
      map.flyTo(center, zoom, { duration: 1.2 });
      prev.current = center;
    }
  }, [center, zoom, map]);
  return null;
}

function MapClickHandler({ active, onMapClick }) {
  useMapEvents({
    click: (e) => {
      if (active) onMapClick(e.latlng);
    },
  });
  return null;
}

function MapCenterTracker({ onCenterChange, onBoundsChange }) {
  const map = useMap();
  useMapEvents({
    moveend: () => {
      const c = map.getCenter();
      onCenterChange([c.lat, c.lng]);
      const b = map.getBounds();
      onBoundsChange({
        lat_min: b.getSouth(),
        lat_max: b.getNorth(),
        lon_min: b.getWest(),
        lon_max: b.getEast(),
      });
    },
  });
  return null;
}

// ─── Saved Locations helpers ────────────────────────────────

function loadSavedLocations() {
  try {
    return JSON.parse(localStorage.getItem(SAVED_LOCATIONS_KEY) || "[]");
  } catch {
    return [];
  }
}

function persistLocations(locs) {
  localStorage.setItem(SAVED_LOCATIONS_KEY, JSON.stringify(locs));
}

// ─── Main Dashboard ─────────────────────────────────────────

export default function DashboardPage() {
  const [selectedDate, setSelectedDate] = useState(getDefaultDate());
  const [layerStates, setLayerStates] = useState({
    ndvi: { enabled: true, opacity: 0.85 },
    truecolor: { enabled: false, opacity: 0.85 },
    lst: { enabled: false, opacity: 0.85 },
  });
  const [layerPanelOpen, setLayerPanelOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [earthquakes, setEarthquakes] = useState(null);
  const [eventsOpen, setEventsOpen] = useState(true);
  // Mobile-only draggable events bottom sheet — "peek" | "half" | "full"
  const [mobileEventsSheet, setMobileEventsSheet] = useState("peek");
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(3);
  const [loading, setLoading] = useState(true);

  // Follow-me GPS mode ─ declared here so `visibleEvents` (below) can filter
  // by proximity to the user's current GPS position.
  const [followMe, setFollowMe] = useState(false);
  const [userGps, setUserGps] = useState(null); // [lat, lon] | null
  const [gpsError, setGpsError] = useState("");

  // While Follow-me is on and a GPS fix has arrived, keep only events within
  // FOLLOW_RADIUS_KM of the user's position so the sidebar shows what's
  // actually relevant to their location.
  const visibleEvents = useMemo(() => {
    if (!followMe || !userGps) return events;
    return events.filter((e) => {
      const geom = e.geometry?.[e.geometry.length - 1];
      const coords = geom?.coordinates;
      if (!coords || coords.length < 2) return false;
      const [lon, lat] = coords;
      return haversineKm(userGps[0], userGps[1], lat, lon) <= FOLLOW_RADIUS_KM;
    });
  }, [events, followMe, userGps]);

  // Feature state
  const [snapshotMode, setSnapshotMode] = useState(false);
  const [snapshotCoords, setSnapshotCoords] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [savedLocations, setSavedLocations] = useState(loadSavedLocations);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [showEarthquakes, setShowEarthquakes] = useState(true);
  const alertsShown = useRef(false);

  // Time-lapse state
  const [timeLapseOpen, setTimeLapseOpen] = useState(false);
  const [downloadModal, setDownloadModal] = useState(null);
  const [mapCenter, setMapCenter] = useState([20, 0]);
  const [mapBounds, setMapBounds] = useState(null);

  // Weather overlay state
  const [weatherEnabled, setWeatherEnabled] = useState(false);
  const [weatherData, setWeatherData] = useState([]);

  // Fullscreen map state (hide header/ticker so map fills the whole viewport)
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const toggleMapFullscreen = () => {
    setMapFullscreen((v) => !v);
    // Trigger Leaflet resize after CSS transition
    setTimeout(() => window.dispatchEvent(new Event("resize")), 260);
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && mapFullscreen) setMapFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mapFullscreen]);

  // Follow-me GPS mode: side-effect for the state declared above.
  useEffect(() => {
    if (!followMe) { setGpsError(""); return; }
    if (!navigator.geolocation) {
      setGpsError("Geolocation is not supported on this device");
      setFollowMe(false);
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setUserGps([pos.coords.latitude, pos.coords.longitude]);
        setGpsError("");
      },
      (err) => {
        setGpsError(err.message || "Could not access GPS");
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [followMe]);

  // Alert settings state
  const [alertSettingsOpen, setAlertSettingsOpen] = useState(false);
  const [alertSettings, setAlertSettings] = useState(loadAlertSettings);

  // Space monitor state
  const [spaceOpen, setSpaceOpen] = useState(false);

  // Derived: primary enabled layer (for legends, snapshots, etc.)
  const primaryLayer = useMemo(
    () => GIBS_LAYERS.find((l) => layerStates[l.id]?.enabled) || GIBS_LAYERS[0],
    [layerStates]
  );
  const enabledLayers = useMemo(
    () => GIBS_LAYERS.filter((l) => layerStates[l.id]?.enabled),
    [layerStates]
  );
  const enabledCount = enabledLayers.length;

  // Fetch EONET events
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/nasa/events?limit=30`);
        setEvents(res.data?.events || []);
      } catch {
        toast.error("Failed to load natural events");
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // Fetch USGS earthquakes
  useEffect(() => {
    const fetchQuakes = async () => {
      try {
        const res = await axios.get(`${API}/usgs/earthquakes?min_magnitude=2.5&days=7&limit=200`);
        setEarthquakes(res.data);
      } catch {
        toast.error("Failed to load earthquake data");
      }
    };
    fetchQuakes();
  }, []);

  // Smart alerts for severe events
  useEffect(() => {
    if (alertsShown.current || !events.length) return;
    alertsShown.current = true;

    const severe = events.filter((e) => {
      const cat = e.categories?.[0]?.id;
      return cat === "volcanoes" || cat === "severeStorms";
    });

    if (severe.length > 0) {
      toast.warning(`${severe.length} severe event${severe.length > 1 ? "s" : ""} detected`, {
        description: severe
          .slice(0, 3)
          .map((e) => e.title)
          .join(", "),
        duration: 6000,
      });
    }
  }, [events]);

  // Smart alerts for large earthquakes
  useEffect(() => {
    if (!earthquakes?.features?.length) return;
    const threshold = alertSettings.earthquake_mag || 6;
    const big = earthquakes.features.filter((f) => (f.properties?.mag || 0) >= threshold);
    if (big.length > 0) {
      toast.warning(`${big.length} earthquake${big.length > 1 ? "s" : ""} >= M${threshold}`, {
        description: big
          .slice(0, 2)
          .map((f) => f.properties.title)
          .join(", "),
        duration: 6000,
      });
    }
  }, [earthquakes, alertSettings.earthquake_mag]);

  // Fetch weather when enabled and map moves
  const weatherTimer = useRef(null);
  useEffect(() => {
    if (!weatherEnabled || !mapBounds) {
      setWeatherData([]);
      return;
    }
    clearTimeout(weatherTimer.current);
    weatherTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/weather/grid`, {
          params: { ...mapBounds, grid_size: 4 },
        });
        const points = res.data?.points || [];
        setWeatherData(points);

        // Check weather thresholds
        const extremes = points.filter((p) =>
          p.temperature >= (alertSettings.temp_max || 42) || p.temperature <= (alertSettings.temp_min || -25)
        );
        if (extremes.length > 0) {
          try {
            await axios.post(`${API}/notifications/check-thresholds`, {
              settings: alertSettings, weather_points: extremes,
            }, { withCredentials: true });
          } catch { /* silent */ }
        }
      } catch {
        // silent
      }
    }, 800);
    return () => clearTimeout(weatherTimer.current);
  }, [weatherEnabled, mapBounds, alertSettings]);

  const handleEventClick = (event) => {
    const geom = event.geometry?.[event.geometry.length - 1];
    if (geom?.coordinates) {
      setFlyTarget([geom.coordinates[1], geom.coordinates[0]]);
      setFlyZoom(6);
    }
  };

  const handleResetView = () => {
    setFlyTarget([20, 0.001]);
    setFlyZoom(3);
  };

  const handleSearchSelect = useCallback(({ lat, lon, name }) => {
    setFlyTarget([lat, lon]);
    setFlyZoom(8);
    toast.success(`Flying to ${name.split(",")[0]}`);
  }, []);

  const handleMapClick = useCallback(
    (latlng) => {
      if (!snapshotMode) return;
      setSnapshotCoords({ lat: latlng.lat, lon: latlng.lng });
      setSnapshotMode(false);
    },
    [snapshotMode]
  );

  const handleSaveLocation = () => {
    const name = prompt("Name this location:");
    if (!name) return;
    const newLoc = {
      id: Date.now().toString(),
      name,
      lat: flyTarget?.[0] || 20,
      lon: flyTarget?.[1] || 0,
    };
    const updated = [...savedLocations, newLoc];
    setSavedLocations(updated);
    persistLocations(updated);
    toast.success(`Saved "${name}"`);
  };

  const handleDeleteLocation = (id) => {
    const updated = savedLocations.filter((l) => l.id !== id);
    setSavedLocations(updated);
    persistLocations(updated);
  };

  const handleGoToSaved = (loc) => {
    setFlyTarget([loc.lat, loc.lon]);
    setFlyZoom(8);
    setBookmarksOpen(false);
  };

  const handleTimeLapseDateChange = useCallback((date) => {
    setSelectedDate(date);
  }, []);

  const handleOpenDownload = useCallback(
    (start, end) => {
      setDownloadModal({ startDate: start, endDate: end, lat: mapCenter[0], lon: mapCenter[1] });
    },
    [mapCenter]
  );

  return (
    <div
      className={`h-screen flex flex-col bg-stone-950 ${snapshotMode ? "cursor-crosshair" : ""}`}
      data-testid="dashboard-page"
    >
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#1c1917",
            border: "1px solid #44403c",
            color: "#fff",
            fontSize: "13px",
          },
        }}
      />

      {/* Header */}
      <header
        className={`bg-stone-900/95 backdrop-blur-md border-b border-stone-800 flex items-center px-4 gap-3 z-[1000] shrink-0 overflow-hidden transition-all duration-200 ${
          mapFullscreen ? "h-0 border-b-0 opacity-0 pointer-events-none" : "h-14"
        }`}
        data-testid="dashboard-header"
      >
        <Link
          to="/"
          className="flex items-center gap-2 text-stone-400 hover:text-white transition-colors"
          data-testid="dashboard-back-link"
        >
          <ArrowLeft size={18} weight="bold" />
          <span className="text-sm font-body hidden sm:inline">Home</span>
        </Link>

        <div className="w-px h-6 bg-stone-700" />

        <div className="flex items-center gap-2">
          <BrandLogo size={28} iconSize={14}
            markClassName="bg-emerald-600"
            nameClassName="font-heading text-sm font-semibold text-white hidden md:inline" />
        </div>

        {/* Page nav links */}
        <div className="hidden lg:flex items-center gap-1">
          {[
            { to: "/data-center", label: "Data" },
            { to: "/trees", label: "Trees" },
            { to: "/carbon-hub", label: "Carbon Hub" },
            { to: "/social", label: "Community" },
            { to: "/admin", label: "Admin" },
          ].map((n) => (
            <Link key={n.to} to={n.to} className="text-xs text-stone-400 hover:text-white px-2 py-1 rounded transition-colors" data-testid={`dash-nav-${n.label.toLowerCase()}`}>
              {n.label}
            </Link>
          ))}
        </div>

        {/* Search */}
        <div className="hidden sm:block">
          <SearchBar onSelectLocation={handleSearchSelect} />
        </div>

        <div className="flex-1" />

        {/* Notification bell */}
        <NotificationBell />

        {/* Date */}
        <div className="flex items-center gap-2 bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5">
          <Calendar size={14} className="text-stone-400" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-transparent text-white text-xs focus:outline-none cursor-pointer"
            data-testid="date-picker"
          />
        </div>

        {/* Multi-layer selector */}
        <div className="relative">
          <button
            onClick={() => setLayerPanelOpen(!layerPanelOpen)}
            className="flex items-center gap-2 bg-stone-800 border border-stone-700 text-white text-xs rounded-lg px-3 py-1.5 hover:bg-stone-700 transition-colors"
            data-testid="layer-selector-btn"
          >
            <Stack size={14} />
            <span className="hidden sm:inline">
              {enabledCount === 0 ? "No layers" : enabledCount === 1 ? primaryLayer.name : `${enabledCount} Layers`}
            </span>
            <CaretDown
              size={12}
              className={`transition-transform ${layerPanelOpen ? "rotate-180" : ""}`}
            />
          </button>
          <AnimatePresence>
            {layerPanelOpen && (
              <MultiLayerPanel
                layers={GIBS_LAYERS}
                layerStates={layerStates}
                onChange={setLayerStates}
                onClose={() => setLayerPanelOpen(false)}
              />
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* Live news ticker */}
      {!mapFullscreen && <NewsTicker userLat={mapCenter[0]} userLon={mapCenter[1]} />}

      {/* Snapshot mode banner */}
      <AnimatePresence>
        {snapshotMode && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-emerald-800/90 border-b border-emerald-700 overflow-hidden z-[999]"
            data-testid="snapshot-mode-banner"
          >
            <div className="flex items-center justify-center gap-3 px-4 py-2">
              <Crosshair size={16} className="text-emerald-200 animate-pulse" />
              <span className="text-sm text-emerald-100 font-body">
                Click anywhere on the map to capture a NASA snapshot
              </span>
              <button
                onClick={() => setSnapshotMode(false)}
                className="text-emerald-200 hover:text-white ml-2"
                data-testid="snapshot-mode-cancel"
              >
                <X size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Map */}
      <div className="flex-1 relative">
        <MapContainer
          center={[20, 0]}
          zoom={3}
          className="h-full w-full"
          zoomControl={false}
          style={{ background: "#1c1917" }}
          data-testid="satellite-map"
        >
          <TileLayer
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {/* Multi-layer GIBS overlays */}
          {enabledLayers.map((l) => (
            <TileLayer
              key={`${l.id}-${selectedDate}`}
              url={getGibsTileUrl(l, selectedDate)}
              opacity={layerStates[l.id].opacity}
              maxNativeZoom={l.maxZoom}
              maxZoom={18}
              tileSize={256}
            />
          ))}

          {/*
            Labels overlay — renders above any NDVI / temperature / true-color
            tint so city / country / river names stay crisp and never get
            masked by dense green vegetation or dark LST colours. Switches
            between sparse country labels (zoom ≤ 8) and dense city / town
            labels (zoom ≥ 9) so field workers can locate villages easily.
          */}
          {(enabledCount > 0) && <AdaptiveLabelsLayer />}

          {/* EONET event markers */}
          {events.map((event) => {
            const geom = event.geometry?.[event.geometry.length - 1];
            if (!geom?.coordinates) return null;
            const color = getEventColor(event.categories);
            return (
              <CircleMarker
                key={event.id}
                center={[geom.coordinates[1], geom.coordinates[0]]}
                radius={7}
                pathOptions={{
                  color,
                  fillColor: color,
                  fillOpacity: 0.6,
                  weight: 2,
                }}
              >
                <Popup>
                  <div className="font-body">
                    <p className="font-heading font-semibold text-sm">{event.title}</p>
                    <p className="text-xs text-stone-500 mt-1">
                      {event.categories?.[0]?.title}
                    </p>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}

          {/* USGS earthquake markers */}
          {showEarthquakes &&
            earthquakes?.features?.map((f) => {
              const coords = f.geometry?.coordinates;
              if (!coords) return null;
              const mag = f.properties?.mag || 0;
              const color = getMagColor(mag);
              return (
                <CircleMarker
                  key={f.id}
                  center={[coords[1], coords[0]]}
                  radius={Math.max(3, mag * 1.5)}
                  pathOptions={{
                    color,
                    fillColor: color,
                    fillOpacity: 0.45,
                    weight: 1.5,
                  }}
                >
                  <Popup>
                    <div className="font-body">
                      <p className="font-heading font-semibold text-sm">
                        M{mag.toFixed(1)}
                      </p>
                      <p className="text-xs text-stone-500 mt-1">
                        {f.properties?.place}
                      </p>
                      {f.properties?.time && (
                        <p className="text-xs text-stone-400 mt-1">
                          {new Date(f.properties.time).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}

          {/* Weather markers */}
          {weatherEnabled &&
            weatherData.map((w, i) => {
              if (w.temperature == null) return null;
              const t = w.temperature;
              const color =
                t <= -10 ? "#1e40af" : t <= 0 ? "#3b82f6" : t <= 10 ? "#06b6d4" :
                t <= 20 ? "#10b981" : t <= 30 ? "#f59e0b" : t <= 40 ? "#ef4444" : "#991b1b";
              return (
                <CircleMarker
                  key={`w-${i}`}
                  center={[w.lat, w.lon]}
                  radius={14}
                  pathOptions={{ color, fillColor: color, fillOpacity: 0.7, weight: 2 }}
                >
                  <Popup>
                    <div className="font-body text-xs space-y-1">
                      <p className="font-heading font-semibold text-sm">{t}°C</p>
                      <p>Feels like: {w.apparent_temperature}°C</p>
                      <p>Wind: {w.wind_speed} km/h</p>
                      <p>Humidity: {w.humidity}%</p>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}

          {flyTarget && <MapFlyTo center={flyTarget} zoom={flyZoom} />}
          <MapClickHandler active={snapshotMode} onMapClick={handleMapClick} />
          <MapCenterTracker onCenterChange={setMapCenter} onBoundsChange={setMapBounds} />
          <FollowMeCenter position={userGps} active={followMe} />
          <ZoomControl />

          {/* Real-time GPS marker while Follow-me is on */}
          {followMe && userGps && (
            <>
              <CircleMarker
                center={userGps}
                radius={7}
                pathOptions={{ color: "#22d3ee", fillColor: "#22d3ee", fillOpacity: 0.9, weight: 2 }}
                data-testid="user-gps-marker"
              >
                <Popup>
                  <div className="text-xs">
                    <p className="font-semibold">Your position</p>
                    <p className="text-stone-500">{userGps[0].toFixed(5)}, {userGps[1].toFixed(5)}</p>
                  </div>
                </Popup>
              </CircleMarker>
              <Circle
                center={userGps}
                radius={FOLLOW_RADIUS_KM * 1000}
                pathOptions={{ color: "#22d3ee", fillColor: "#22d3ee", fillOpacity: 0.05, weight: 1, dashArray: "4 4" }}
              />
            </>
          )}

          {/* Space overlay (ISS, Sun, Moon, Terminator) */}
          <SpaceMapOverlay active={spaceOpen} />
        </MapContainer>

        {/* ── Left side controls ── */}
        <div className="absolute top-4 left-2 sm:left-4 z-[900] flex flex-col gap-2 max-w-[40vw] sm:max-w-none">
          {/* Active layer info */}
          <div
            className="bg-stone-900/90 backdrop-blur-md rounded-xl px-3 sm:px-4 py-2 sm:py-3 border border-stone-700 flex items-center gap-2 sm:gap-3"
            data-testid="active-layer-info"
          >
            <div
              className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg ${primaryLayer.accentBg} flex items-center justify-center shrink-0`}
            >
              <primaryLayer.Icon size={14} weight="bold" className={primaryLayer.accent} />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] sm:text-xs text-white font-heading font-medium truncate">
                {enabledCount > 1 ? `${enabledCount} Layers` : primaryLayer.name}
              </p>
              <p className="text-[9px] sm:text-[10px] text-stone-500">{selectedDate}</p>
            </div>
          </div>

          {/* Tool buttons (vertical on desktop, hidden on mobile — see bottom drawer below) */}
          <div className="hidden sm:flex flex-col gap-1.5">
            <button
              onClick={() => setSnapshotMode(!snapshotMode)}
              className={`bg-stone-900/90 backdrop-blur-md border rounded-xl p-2.5 transition-colors ${
                snapshotMode
                  ? "border-emerald-500 text-emerald-400"
                  : "border-stone-700 text-stone-400 hover:text-white hover:bg-stone-800"
              }`}
              data-testid="snapshot-mode-btn"
              title="Capture NASA Snapshot"
            >
              <Camera size={18} weight="bold" />
            </button>
            <button
              onClick={() => setAnalyticsOpen(!analyticsOpen)}
              className={`bg-stone-900/90 backdrop-blur-md border rounded-xl p-2.5 transition-colors ${
                analyticsOpen
                  ? "border-emerald-500 text-emerald-400"
                  : "border-stone-700 text-stone-400 hover:text-white hover:bg-stone-800"
              }`}
              data-testid="analytics-toggle-btn"
              title="Analytics"
            >
              <ChartBar size={18} weight="bold" />
            </button>
            <button
              onClick={() => setShowEarthquakes(!showEarthquakes)}
              className={`bg-stone-900/90 backdrop-blur-md border rounded-xl p-2.5 transition-colors ${
                showEarthquakes
                  ? "border-orange-500 text-orange-400"
                  : "border-stone-700 text-stone-400 hover:text-white hover:bg-stone-800"
              }`}
              data-testid="earthquakes-toggle-btn"
              title={showEarthquakes ? "Hide Earthquakes" : "Show Earthquakes"}
            >
              <Mountains size={18} weight="bold" />
            </button>
            <button
              onClick={() => setTimeLapseOpen(!timeLapseOpen)}
              className={`bg-stone-900/90 backdrop-blur-md border rounded-xl p-2.5 transition-colors ${
                timeLapseOpen
                  ? "border-emerald-500 text-emerald-400"
                  : "border-stone-700 text-stone-400 hover:text-white hover:bg-stone-800"
              }`}
              data-testid="timelapse-btn"
              title="Time-Lapse Mode"
            >
              <Timer size={18} weight="bold" />
            </button>
            <button
              onClick={() => setWeatherEnabled(!weatherEnabled)}
              className={`bg-stone-900/90 backdrop-blur-md border rounded-xl p-2.5 transition-colors ${
                weatherEnabled
                  ? "border-sky-500 text-sky-400"
                  : "border-stone-700 text-stone-400 hover:text-white hover:bg-stone-800"
              }`}
              data-testid="weather-toggle-btn"
              title={weatherEnabled ? "Hide Weather" : "Show Weather"}
            >
              <CloudSun size={18} weight="bold" />
            </button>
            <button
              onClick={() => setAlertSettingsOpen(true)}
              className="bg-stone-900/90 backdrop-blur-md border border-stone-700 rounded-xl p-2.5 text-stone-400 hover:text-amber-400 hover:bg-stone-800 transition-colors"
              data-testid="alert-settings-btn"
              title="Alert Settings"
            >
              <Bell size={18} weight="bold" />
            </button>
            <button
              onClick={() => setSpaceOpen(!spaceOpen)}
              className={`bg-stone-900/90 backdrop-blur-md border rounded-xl p-2.5 transition-colors ${
                spaceOpen
                  ? "border-amber-500 text-amber-400"
                  : "border-stone-700 text-stone-400 hover:text-white hover:bg-stone-800"
              }`}
              data-testid="space-btn"
              title="Space & Solar Monitor"
            >
              <Rocket size={18} weight="bold" />
            </button>
            <div className="relative">
              <button
                onClick={() => setBookmarksOpen(!bookmarksOpen)}
                className={`bg-stone-900/90 backdrop-blur-md border rounded-xl p-2.5 transition-colors ${
                  bookmarksOpen
                    ? "border-emerald-500 text-emerald-400"
                    : "border-stone-700 text-stone-400 hover:text-white hover:bg-stone-800"
                }`}
                data-testid="bookmarks-btn"
                title="Saved Locations"
              >
                <BookmarkSimple size={18} weight="bold" />
              </button>
              <AnimatePresence>
                {bookmarksOpen && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    className="absolute top-0 left-full ml-2 w-56 bg-stone-900/95 backdrop-blur-md border border-stone-700 rounded-xl shadow-2xl overflow-hidden"
                    data-testid="bookmarks-panel"
                  >
                    <div className="px-3 py-2 border-b border-stone-800 flex items-center justify-between">
                      <span className="text-xs text-white font-heading">Saved Locations</span>
                      <button
                        onClick={handleSaveLocation}
                        className="text-emerald-400 hover:text-emerald-300 p-0.5"
                        data-testid="save-location-btn"
                        title="Save current location"
                      >
                        <Plus size={14} weight="bold" />
                      </button>
                    </div>
                    {savedLocations.length === 0 ? (
                      <div className="px-3 py-4 text-center text-xs text-stone-500">
                        No saved locations
                      </div>
                    ) : (
                      <div className="max-h-48 overflow-y-auto">
                        {savedLocations.map((loc) => (
                          <div
                            key={loc.id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-stone-800/60 transition-colors group"
                          >
                            <button
                              onClick={() => handleGoToSaved(loc)}
                              className="flex-1 text-left min-w-0"
                              data-testid={`saved-location-${loc.id}`}
                            >
                              <p className="text-xs text-white truncate">{loc.name}</p>
                              <p className="text-[10px] text-stone-500">
                                {loc.lat.toFixed(2)}, {loc.lon.toFixed(2)}
                              </p>
                            </button>
                            <button
                              onClick={() => handleDeleteLocation(loc.id)}
                              className="opacity-0 group-hover:opacity-100 text-stone-500 hover:text-red-400 transition-all p-0.5"
                              data-testid={`delete-location-${loc.id}`}
                            >
                              <Trash size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <button
              onClick={() => setFollowMe((v) => !v)}
              className={`bg-stone-900/90 backdrop-blur-md border rounded-xl p-2.5 transition-colors ${
                followMe
                  ? "border-cyan-500 text-cyan-400 bg-cyan-500/10"
                  : "border-stone-700 text-stone-400 hover:text-white hover:bg-stone-800"
              }`}
              data-testid="followme-btn"
              title={followMe ? "Stop following my location" : "Follow my location (GPS)"}
            >
              <Crosshair size={18} weight={followMe ? "fill" : "bold"} />
            </button>
            <button
              onClick={handleResetView}
              className="bg-stone-900/90 backdrop-blur-md border border-stone-700 rounded-xl p-2.5 text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
              data-testid="reset-view-btn"
              title="Reset view to global"
            >
              <Compass size={18} weight="bold" />
            </button>
            <button
              onClick={toggleMapFullscreen}
              className="bg-stone-900/90 backdrop-blur-md border border-stone-700 rounded-xl p-2.5 text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
              data-testid="fullscreen-toggle-btn"
              title={mapFullscreen ? "Exit fullscreen" : "Enter fullscreen map"}
            >
              {mapFullscreen ? <ArrowsIn size={18} weight="bold" /> : <ArrowsOut size={18} weight="bold" />}
            </button>
          </div>
        </div>

        {/* ── Zoom + / − buttons (both mobile and desktop, right side) ── */}
        {/* Rendered *inside* the MapContainer via <ZoomControlPanel/> below so
            we can use `useMap()` — kept in JSX flow here for readability only. */}

        {/* ── Mobile bottom toolbar (phones only) ── */}
        {!mapFullscreen && (
          <div
            className="sm:hidden absolute bottom-0 left-0 right-0 z-[900] bg-stone-900/95 backdrop-blur-md border-t border-stone-800"
            data-testid="mobile-toolbar"
          >
            <div className="flex items-center gap-1.5 px-2 py-2 overflow-x-auto scrollbar-thin">
              <MobileToolBtn active={snapshotMode} activeColor="emerald"
                onClick={() => setSnapshotMode(!snapshotMode)} label="Snapshot"
                testId="mobile-snapshot-btn">
                <Camera size={16} weight="bold" />
              </MobileToolBtn>
              <MobileToolBtn active={analyticsOpen} activeColor="emerald"
                onClick={() => setAnalyticsOpen(!analyticsOpen)} label="Chart"
                testId="mobile-analytics-btn">
                <ChartBar size={16} weight="bold" />
              </MobileToolBtn>
              <MobileToolBtn active={showEarthquakes} activeColor="orange"
                onClick={() => setShowEarthquakes(!showEarthquakes)} label="Quakes"
                testId="mobile-quakes-btn">
                <Mountains size={16} weight="bold" />
              </MobileToolBtn>
              <MobileToolBtn active={timeLapseOpen} activeColor="emerald"
                onClick={() => setTimeLapseOpen(!timeLapseOpen)} label="Time-lapse"
                testId="mobile-timelapse-btn">
                <Timer size={16} weight="bold" />
              </MobileToolBtn>
              <MobileToolBtn active={weatherEnabled} activeColor="sky"
                onClick={() => setWeatherEnabled(!weatherEnabled)} label="Weather"
                testId="mobile-weather-btn">
                <CloudSun size={16} weight="bold" />
              </MobileToolBtn>
              <MobileToolBtn active={false} activeColor="amber"
                onClick={() => setAlertSettingsOpen(true)} label="Alerts"
                testId="mobile-alerts-btn">
                <Bell size={16} weight="bold" />
              </MobileToolBtn>
              <MobileToolBtn active={spaceOpen} activeColor="amber"
                onClick={() => setSpaceOpen(!spaceOpen)} label="Space"
                testId="mobile-space-btn">
                <Rocket size={16} weight="bold" />
              </MobileToolBtn>
              <MobileToolBtn active={bookmarksOpen} activeColor="emerald"
                onClick={() => setBookmarksOpen(!bookmarksOpen)} label="Saved"
                testId="mobile-bookmarks-btn">
                <BookmarkSimple size={16} weight="bold" />
              </MobileToolBtn>
              <MobileToolBtn active={followMe} activeColor="sky"
                onClick={() => setFollowMe((v) => !v)} label="Follow"
                testId="mobile-followme-btn">
                <Crosshair size={16} weight={followMe ? "fill" : "bold"} />
              </MobileToolBtn>
              <MobileToolBtn active={false} activeColor="stone"
                onClick={handleResetView} label="Reset"
                testId="mobile-reset-btn">
                <Compass size={16} weight="bold" />
              </MobileToolBtn>
              <MobileToolBtn active={mapFullscreen} activeColor="stone"
                onClick={toggleMapFullscreen} label={mapFullscreen ? "Exit" : "Full"}
                testId="mobile-fullscreen-btn">
                {mapFullscreen ? <ArrowsIn size={16} weight="bold" /> : <ArrowsOut size={16} weight="bold" />}
              </MobileToolBtn>
            </div>
          </div>
        )}
        <AnimatePresence>
          {layerStates.ndvi?.enabled && !analyticsOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="absolute bottom-6 sm:bottom-6 left-1/2 -translate-x-1/2 z-[900] bg-stone-900/90 backdrop-blur-md rounded-xl px-6 py-3 border border-stone-700 mb-16 sm:mb-0"
              data-testid="ndvi-legend"
            >
              <p className="text-[11px] text-stone-400 mb-2 text-center font-body tracking-wide uppercase">
                NDVI Vegetation Scale
              </p>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-stone-500 w-4 text-right">-1</span>
                <div
                  className="w-52 h-3 rounded-full"
                  style={{
                    background:
                      "linear-gradient(to right, #6b3a2a, #c4a265, #e8d44d, #7bc043, #1a6b3a, #003d1e)",
                  }}
                />
                <span className="text-[10px] text-stone-500 w-4">+1</span>
              </div>
              <div className="flex justify-between text-[9px] text-stone-500 mt-1 px-6">
                <span>Water/Bare</span>
                <span>Sparse</span>
                <span>Dense</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── LST Legend ── */}
        <AnimatePresence>
          {layerStates.lst?.enabled && !analyticsOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[900] bg-stone-900/90 backdrop-blur-md rounded-xl px-6 py-3 border border-stone-700 mb-16 sm:mb-0"
              data-testid="lst-legend"
            >
              <p className="text-[11px] text-stone-400 mb-2 text-center font-body tracking-wide uppercase">
                Land Surface Temperature
              </p>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-stone-500 w-8 text-right">-25°C</span>
                <div
                  className="w-48 h-3 rounded-full"
                  style={{
                    background:
                      "linear-gradient(to right, #0000ff, #00ffff, #00ff00, #ffff00, #ff8800, #ff0000)",
                  }}
                />
                <span className="text-[10px] text-stone-500 w-8">+45°C</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Events Panel (mobile bottom sheet, drag-to-expand) ── */}
        {!mapFullscreen && (
          <MobileEventsSheet
            state={mobileEventsSheet}
            setState={setMobileEventsSheet}
            events={visibleEvents}
            loading={loading}
            onEventClick={handleEventClick}
          />
        )}

        {/* ── Events Panel (desktop) ── */}
        <div className="hidden sm:block absolute top-4 right-2 sm:right-4 z-[900] w-56 sm:w-72 max-w-[calc(100vw-1rem)]" data-testid="events-panel">
          <button
            onClick={() => setEventsOpen(!eventsOpen)}
            className="w-full bg-stone-900/90 backdrop-blur-md rounded-xl px-4 py-3 border border-stone-700 flex items-center justify-between text-white hover:bg-stone-800/90 transition-colors"
            data-testid="events-toggle"
          >
            <div className="flex items-center gap-2">
              <Lightning size={16} weight="bold" className="text-amber-400" />
              <span className="text-sm font-heading">
                {followMe && userGps ? "Nearby Events" : "Events"}
                {visibleEvents.length > 0 && (
                  <span className="ml-1 text-xs text-stone-500">
                    ({visibleEvents.length}{followMe && userGps ? ` / ${events.length}` : ""})
                  </span>
                )}
              </span>
            </div>
            {eventsOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
          </button>

          <AnimatePresence>
            {eventsOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-2 bg-stone-900/90 backdrop-blur-md rounded-xl border border-stone-700 overflow-hidden"
              >
                {loading ? (
                  <div className="px-4 py-8 text-center">
                    <div className="w-6 h-6 border-2 border-stone-600 border-t-emerald-400 rounded-full animate-spin mx-auto" />
                    <p className="text-xs text-stone-500 mt-3 font-body">Loading events...</p>
                  </div>
                ) : visibleEvents.length === 0 ? (
                  <div className="px-4 py-6 text-center">
                    <p className="text-xs text-stone-500 font-body">
                      {followMe && userGps
                        ? `No active events within ${FOLLOW_RADIUS_KM} km of you`
                        : "No active events"}
                    </p>
                  </div>
                ) : (
                  <div className="max-h-[40vh] overflow-y-auto">
                    {visibleEvents.slice(0, 15).map((event) => {
                      const color = getEventColor(event.categories);
                      return (
                        <button
                          key={event.id}
                          className="w-full text-left px-4 py-2.5 hover:bg-stone-800/60 border-b border-stone-800/50 last:border-0 transition-colors flex items-start gap-3"
                          onClick={() => handleEventClick(event)}
                          data-testid={`event-item-${event.id}`}
                        >
                          <div
                            className="w-2.5 h-2.5 rounded-full mt-1 shrink-0"
                            style={{ backgroundColor: color }}
                          />
                          <div className="min-w-0">
                            <p className="text-sm text-white font-body truncate">
                              {event.title}
                            </p>
                            <p className="text-[11px] text-stone-500 mt-0.5">
                              {event.categories?.[0]?.title}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Attribution ── */}
        <div className="absolute bottom-6 right-4 z-[900]">
          <div className="bg-stone-900/70 backdrop-blur-sm rounded-lg px-3 py-1.5 flex items-center gap-1.5">
            <Info size={12} className="text-stone-500" />
            <span className="text-[10px] text-stone-500 font-body">
              NASA GIBS · NASA EONET · USGS
            </span>
          </div>
        </div>

        {/* ── Space Panel ── */}
        <AnimatePresence>
          {spaceOpen && (
            <SpacePanel open={spaceOpen} onClose={() => setSpaceOpen(false)} />
          )}
        </AnimatePresence>

        {/* ── Analytics Drawer ── */}
        <AnimatePresence>
          {analyticsOpen && (
            <AnalyticsDrawer
              open={analyticsOpen}
              onClose={() => setAnalyticsOpen(false)}
              events={events}
              earthquakes={earthquakes}
            />
          )}
        </AnimatePresence>

        {/* ── Time-Lapse Control ── */}
        <AnimatePresence>
          {timeLapseOpen && (
            <TimeLapseControl
              currentDate={selectedDate}
              onDateChange={handleTimeLapseDateChange}
              onClose={() => setTimeLapseOpen(false)}
              onDownload={handleOpenDownload}
              activeLayerId={primaryLayer.id}
            />
          )}
        </AnimatePresence>
      </div>

      {/* ── Snapshot Modal ── */}
      <AnimatePresence>
        {snapshotCoords && (
          <SnapshotModal
            lat={snapshotCoords.lat}
            lon={snapshotCoords.lon}
            date={selectedDate}
            layerName={primaryLayer.layer}
            onClose={() => setSnapshotCoords(null)}
          />
        )}
      </AnimatePresence>

      {/* ── Download Modal ── */}
      <AnimatePresence>
        {downloadModal && (
          <DownloadModal
            startDate={downloadModal.startDate}
            endDate={downloadModal.endDate}
            lat={downloadModal.lat}
            lon={downloadModal.lon}
            activeLayer={primaryLayer.id}
            onClose={() => setDownloadModal(null)}
          />
        )}
      </AnimatePresence>

      {/* ── Alert Settings Modal ── */}
      <AnimatePresence>
        {alertSettingsOpen && (
          <AlertSettings
            settings={alertSettings}
            onSave={setAlertSettings}
            onClose={() => setAlertSettingsOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
